import { apiClient } from "@/lib/api/client";

export type DataUsageSummaryDto = {
  admin_user_id: string | null;
  admin_name: string | null;
  admin_email: string;
  storage_limit_gb: number;
  storage_limit_bytes: number;
  storage_limit_label: string;
  assigned_devices_count: number;
  telemetry_bytes: number;
  alerts_bytes: number;
  ota_files_bytes: number;
  media_bytes: number;
  backups_bytes: number;
  total_bytes: number;
  usage_percent: number;
  status: "normal" | "warning" | "over_limit" | string;
  quota_locked: boolean;
  tenant_id: string | null;
  tenant_name: string | null;
  workspace_ids: string[];
  workspace_names: string[];
  workspace_count: number;
};

export type StorageQuotaStatusDto = {
  tenant_id: string | null;
  tenant_name: string | null;
  storage_limit_label: string;
  storage_limit_bytes: number;
  total_bytes: number;
  usage_percent: number;
  quota_locked: boolean;
  status: "normal" | "warning" | "over_limit" | string;
};

export type SuperAdminLockedWorkspaceItemDto = {
  tenant_id: string;
  tenant_name: string;
  admin_user_id: string | null;
  admin_name: string | null;
  admin_email: string | null;
  usage_percent: number;
  storage_limit_label: string;
};

export type SuperAdminLockedWorkspaceSummaryDto = {
  locked_workspace_count: number;
  items: SuperAdminLockedWorkspaceItemDto[];
};

export type SuperAdminAdminStorageRowDto = {
  admin_user_id: string | null;
  admin_name: string | null;
  admin_email: string | null;
  tenant_id: string;
  tenant_name: string;
  workspace_ids: string[];
  workspace_names: string[];
  total_bytes: number;
  storage_limit_gb: number;
  storage_limit_label: string;
  usage_percent: number;
  quota_status: "normal" | "warning" | "locked" | string;
  assigned_devices_count: number;
};

export type SuperAdminAdminStoragePageDto = {
  items: SuperAdminAdminStorageRowDto[];
  total: number;
};

export type StorageItemSummaryDto = {
  key: "telemetry" | "alerts" | "ota_files" | "media";
  label: string;
  bytes_used: number;
  record_count: number;
  item_count: number;
  supports_export_delete: boolean;
  supports_simple_delete: boolean;
  quota_applies: boolean;
};

export type StorageItemDetailDto = {
  key: string;
  title: string;
  entries: Array<Record<string, unknown>>;
};

export type StorageCleanupRequestDto = {
  mode: "before_date" | "date_range" | "oldest_records";
  before_date?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  record_count?: number | null;
};

export type StorageCleanupResultDto = {
  key: string;
  deleted_count: number;
  bytes_reclaimed: number;
  quota_locked: boolean;
  message: string;
};

export type StorageArtifactDeleteResultDto = {
  key: string;
  deleted_count: number;
  blocked_count: number;
  bytes_reclaimed: number;
  quota_locked: boolean;
  message: string;
};

export type BackupJobDto = {
  backup_id: string;
  status: string;
  backup_type: string;
  message: string;
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function filenameFromDisposition(value: string | undefined, fallback: string): string {
  const match = value?.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? fallback;
}

function safeBackupFilename(value?: string | null): string {
  const stem = String(value || "device")
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/^[-._]+|[-._]+$/g, "")
    .slice(0, 120) || "device";
  return `${stem}-backup.zip`;
}

export async function getMyDataUsage(tenantId?: string | null, adminUserId?: string | null): Promise<DataUsageSummaryDto> {
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  if (adminUserId) params.admin_user_id = adminUserId;
  const { data } = await apiClient.get<DataUsageSummaryDto>("/data-management/usage/me", {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function getQuotaStatus(tenantId?: string | null): Promise<StorageQuotaStatusDto> {
  const { data } = await apiClient.get<StorageQuotaStatusDto>("/data-management/quota-status", {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  });
  return data;
}

export async function getSuperAdminLockedWorkspaceSummary(): Promise<SuperAdminLockedWorkspaceSummaryDto> {
  const { data } = await apiClient.get<SuperAdminLockedWorkspaceSummaryDto>("/data-management/super-admin/locked-workspaces");
  return data;
}

export async function listSuperAdminAdminStorage(options?: {
  search?: string | null;
  status?: string | null;
  limit?: number;
  offset?: number;
}): Promise<SuperAdminAdminStoragePageDto> {
  const params: Record<string, string | number> = {};
  if (options?.search?.trim()) params.search = options.search.trim();
  if (options?.status?.trim()) params.status = options.status.trim();
  if (typeof options?.limit === "number") params.limit = options.limit;
  if (typeof options?.offset === "number") params.offset = options.offset;
  const { data } = await apiClient.get<SuperAdminAdminStoragePageDto>("/data-management/super-admin/admin-storage", {
    params,
  });
  return data;
}

export async function listStorageItems(tenantId?: string | null): Promise<StorageItemSummaryDto[]> {
  const { data } = await apiClient.get<StorageItemSummaryDto[]>("/data-management/storage-items", {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  });
  return data;
}

export async function getStorageItemDetail(itemKey: string, tenantId?: string | null): Promise<StorageItemDetailDto> {
  const { data } = await apiClient.get<StorageItemDetailDto>(`/data-management/storage-items/${itemKey}`, {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  });
  return data;
}

export async function deleteStorageRows(itemKey: string, body: StorageCleanupRequestDto, tenantId?: string | null): Promise<StorageCleanupResultDto> {
  const { data } = await apiClient.post<StorageCleanupResultDto>(`/data-management/storage-items/${itemKey}/delete`, body, {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  });
  return data;
}

export async function exportDeleteStorageRows(itemKey: string, body: StorageCleanupRequestDto, tenantId?: string | null): Promise<void> {
  const response = await apiClient.post<Blob>(`/data-management/storage-items/${itemKey}/export-delete`, body, {
    params: tenantId ? { tenant_id: tenantId } : undefined,
    responseType: "blob",
  });
  downloadBlob(response.data, filenameFromDisposition(response.headers["content-disposition"], `${itemKey}-cleanup.csv`));
}

export async function deleteStorageArtifacts(itemKey: string, ids: string[], tenantId?: string | null): Promise<StorageArtifactDeleteResultDto> {
  const { data } = await apiClient.post<StorageArtifactDeleteResultDto>(`/data-management/storage-items/${itemKey}/artifacts/delete`, { ids }, {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  });
  return data;
}

export async function backupDevice(deviceId: string): Promise<BackupJobDto> {
  const { data } = await apiClient.post<BackupJobDto>(`/data-management/devices/${deviceId}/backup`);
  return data;
}

export async function backupAndDeleteDevice(deviceId: string): Promise<BackupJobDto> {
  const { data } = await apiClient.post<BackupJobDto>(`/data-management/devices/${deviceId}/backup-delete`);
  return data;
}

export async function downloadBackup(backupId: string, token: string, fallbackName?: string | null): Promise<void> {
  const response = await apiClient.get<Blob>(`/data-management/backups/${backupId}/download`, {
    params: { token },
    responseType: "blob",
  });
  downloadBlob(response.data, filenameFromDisposition(response.headers["content-disposition"], safeBackupFilename(fallbackName)));
}
